import { useState, useEffect, useCallback } from 'react';
import { useUser } from '../providers/UserProvider';
import { ReaderNavbar } from '../components/ReaderNavbar';
import { Toast } from '../components/Toast';
import { DigestSubscriptionMenu } from '../components/DigestSubscriptionMenu';
import {
  listDigests,
  listDigestCadences,
  type Digest,
  type DigestCadenceOption,
} from '../repositories/digestRepository';
import { listSavedSearches, type SavedSearch } from '../repositories/savedSearchRepository';
import { formatRelativeTime } from '../utils/formatTime';

/**
 * Email digests, one row per feed. The page deliberately lists every feed
 * rather than only the subscribed ones: subscribing is the thing people come
 * here to do, and a create-form-plus-list would have shown the same feed in
 * two places with two different controls.
 *
 * The route path is pinned to the backend's frontend_links.DIGESTS_PATH by
 * test_frontend_route_parity.py -- every digest email's footer links here, so
 * renaming one side without the other breaks the unsubscribe link in mail
 * already sent.
 */
export default function DigestsPage() {
  const { user, logout } = useUser();
  const [searches, setSearches] = useState<SavedSearch[]>([]);
  const [digestsBySearch, setDigestsBySearch] = useState<Record<string, Digest>>({});
  const [cadences, setCadences] = useState<DigestCadenceOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState({ message: '', isError: false });

  const loadDigests = useCallback(async () => {
    const rows = await listDigests();
    setDigestsBySearch(Object.fromEntries(rows.map((d) => [d.description_id, d])));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [loadedSearches, loadedCadences] = await Promise.all([
        listSavedSearches(),
        listDigestCadences(),
      ]);
      setSearches(loadedSearches);
      setCadences(loadedCadences);
      await loadDigests();
    } catch (err) {
      setStatus({ message: (err as Error).message || 'Failed to load', isError: true });
    } finally {
      setLoading(false);
    }
  }, [loadDigests]);

  useEffect(() => { load(); }, [load]);

  const dismissStatus = useCallback(() => setStatus({ message: '', isError: false }), []);

  const subscriptionMeta = (digest: Digest | undefined) => {
    if (!digest) return 'No email subscription';
    const cadence = cadences.find((c) => c.value === digest.cadence);
    const parts = [
      digest.last_sent_at ? `Last sent ${formatRelativeTime(digest.last_sent_at)}` : 'Not sent yet',
    ];
    if (cadence) parts.push(`${cadence.article_count} stories per email`);
    if (digest.articles_sent) parts.push(`${digest.articles_sent} sent so far`);
    return parts.join(' · ');
  };

  return (
    <div className="reader-shell">
      <Toast message={status.message} isError={status.isError} onDismiss={dismissStatus} />
      <ReaderNavbar
        selectedChannelId=""
        onSelectChannel={() => {}}
        user={user}
        onLogout={logout}
      />
      <main className="reader-main-full">
        <section className="feed-list-pane">
          <div className="saved-searches-page">
            <div className="saved-searches-header">
              <h1 className="saved-searches-title">Email digests</h1>
            </div>
            <p className="digest-intro">
              Get the top-ranked stories from any of your feeds by email. A story you have
              already been sent is never repeated.
            </p>

            {loading ? (
              <div className="empty-panel">Loading...</div>
            ) : searches.length === 0 ? (
              <div className="empty-panel">
                No feeds yet. Create one under My feeds, then subscribe to it here.
              </div>
            ) : (
              <div className="saved-searches-list">
                {searches.map((s) => (
                  <div key={s.id} className="saved-search-card">
                    <div className="saved-search-card-display">
                      <div className="saved-search-card-main">
                        <div className="saved-search-card-name">{s.name}</div>
                        <div className="saved-search-card-description">{s.description}</div>
                        <div className="saved-search-card-meta">
                          <span>{subscriptionMeta(digestsBySearch[s.id])}</span>
                        </div>
                      </div>
                      <div className="saved-search-card-actions">
                        <DigestSubscriptionMenu
                          descriptionId={s.id}
                          digest={digestsBySearch[s.id]}
                          cadences={cadences}
                          onChanged={loadDigests}
                          onError={(message) => setStatus({ message, isError: true })}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>
    </div>
  );
}
